import produce from 'immer';
import message from 'antd/es/message';
import { InfiniteData, useMutation } from 'react-query';

import { errorToString, queryClient, request, useCurrentUserId, toastError } from '@/utils';

import { setQueryAllFeedDetailByArticleId } from '../../hooks';
import { useCommentOrder } from '../../state';
import { FeedArticleDetail, FeedComment } from '../../types';

export const usePinCommentOperate = ({
  action,
  commentId,
}: {
  action: 'pin' | 'unpin';
  commentId: number;
}) => {
  const currentId = useCurrentUserId();
  const isPinAction = action === 'pin';
  return useMutation(
    () => {
      const url = isPinAction ? '/article_comment/pin' : '/article_comment/unpin';
      return request.post<{ is_success: boolean }>(url, {
        id: commentId,
      });
    },
    {
      onSuccess(res) {
        if (!res?.is_success) {
          toastError(res);
          return;
        } else {
          message.success(isPinAction ? 'pinned' : 'unpinned');
        }
        const postStreamKey = ['/article/comments', currentId];
        queryClient.refetchQueries(postStreamKey);
      },
      onError(err: any) {
        toastError(err);
      },
    },
  );
};

// hide comment
export const useHideComment = ({
  articleId,
  commentId,
  isLowQuality,
}: {
  articleId: number;
  commentId: number;
  isLowQuality: boolean;
}) => {
  const currentAddress = useCurrentUserId();
  const [commentOrderBy] = useCommentOrder();

  return useMutation(
    () => {
      return request.post<{ is_success: boolean }>('/article/delete_comment', {
        comment_id: commentId,
      });
    },
    {
      onSuccess(res) {
        if (!res?.is_success) {
          message.error('Failed');

          return;
        } else {
          message.success('Hidden');
        }

        const recipe = (pre?: FeedArticleDetail) => {
          if (pre) {
            pre.article.comment_count = (pre.article.comment_count || 1) - 1;

            // 因为不用考虑翻页，直接在 artcile 对象中移除对应的 comment
            pre.comments = pre.comments?.filter(x => x.id !== commentId);
          }
        };

        setQueryAllFeedDetailByArticleId({
          currentAddress: currentAddress!,
          articleId,
          recipe,
        });

        const commentQueryKey = [
          '/article/comments',
          currentAddress,
          commentOrderBy,
          articleId,
          { isLowQuality },
        ];
        queryClient.setQueriesData<
          InfiniteData<{ comments: FeedComment[]; total_count: number }> | undefined
        >(commentQueryKey, pre => {
          if (!pre) {
            return pre;
          }

          return produce(pre, draft => {
            if (pre) {
              draft?.pages?.find(page => {
                const record = page?.comments?.find(n => n.id === commentId);
                page.total_count = (page.total_count || 1) - 1;
                if (record) {
                  record._is_hidden = true;
                  return true;
                }
                return false;
              });
            }
          });
        });
      },
      onError(err: any) {
        message.error(errorToString(err) || 'Failed');
      },
    },
  );
};
